import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const SafetyTipsCarousel = () => {
  const [currentTip, setCurrentTip] = useState(0);

  const safetyTips = [
    {
      id: 1,
      title: 'Meet in Public Places',
      icon: 'MapPin',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      description: 'Always choose public, well-lit locations for first meetings. Coffee shops, restaurants, or busy parks are ideal choices.',
      tips: [
        'Arrive and leave independently',
        'Let a friend know where you\'ll be',
        'Stay in populated areas'
      ]
    },
    {
      id: 2,
      title: 'Protect Your Personal Information',
      icon: 'Lock',
      color: 'text-success',
      bgColor: 'bg-success/10',
      description: 'Keep sensitive information private until you\'ve built trust. Your safety depends on maintaining boundaries.',
      tips: [
        'Don\'t share your home address early',
        'Use the platform\'s messaging system',
        'Avoid sharing financial details',
        'Be cautious with social media links'
      ]
    },
    {
      id: 3,
      title: 'Trust Your Instincts',
      icon: 'AlertCircle',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      description: 'If something feels off, it probably is. Don\'t ignore red flags or pressure yourself to continue uncomfortable interactions.',
      tips: [
        'End conversations that make you uncomfortable',
        'Block users who ignore your boundaries',
        'Report suspicious behavior immediately',
        'Don\'t feel obligated to explain yourself'
      ]
    },
    {
      id: 4,
      title: 'Recognize Red Flags',
      icon: 'Flag',
      color: 'text-error',
      bgColor: 'bg-error/10',
      description: 'Be aware of warning signs that someone may not have good intentions. Stay vigilant and protect yourself.',
      tips: [
        'Requests for money or financial help',
        'Pressure to move off the platform quickly',
        'Inconsistent stories or information',
        'Refusal to video chat or meet in person',
        'Overly aggressive or sexual messages'
      ]
    },
    {
      id: 5,
      title: 'Tell Someone Your Plans',
      icon: 'Users',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      description: 'Always inform a trusted friend or family member about your dating plans. Share details and check in regularly.',
      tips: [
        'Share location, time, and who you\'re meeting',
        'Set up check-in times',
        'Have an exit strategy ready',
        'Keep your phone charged'
      ]
    },
    {
      id: 6,
      title: 'Stay Sober and Alert',
      icon: 'Eye',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      description: 'Keep your wits about you during dates. Staying alert helps you make better decisions and stay safe.',
      tips: [
        'Watch your drink being prepared',
        'Never leave drinks unattended',
        'Limit alcohol consumption',
        'Stay aware of your surroundings'
      ]
    }
  ];

  const nextTip = () => {
    setCurrentTip((prev) => (prev + 1) % safetyTips?.length);
  };

  const prevTip = () => {
    setCurrentTip((prev) => (prev - 1 + safetyTips?.length) % safetyTips?.length);
  };

  const tip = safetyTips?.[currentTip];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Safety Tips</h2>
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={prevTip}
            iconName="ChevronLeft"
          />
          <span className="text-sm text-muted-foreground">
            {currentTip + 1} / {safetyTips?.length}
          </span>
          <Button
            size="icon"
            variant="outline"
            onClick={nextTip}
            iconName="ChevronRight"
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-4">
          <div className={cn("p-3 rounded-lg", tip?.bgColor)}>
            <Icon name={tip?.icon} size={32} className={tip?.color} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">{tip?.title}</h3>
            <p className="text-sm text-muted-foreground mb-4">{tip?.description}</p>
            
            <div className="space-y-2">
              {tip?.tips?.map((tipItem, index) => (
                <div key={index} className="flex gap-2">
                  <Icon name="Check" size={16} className={cn("mt-0.5", tip?.color)} />
                  <span className="text-sm text-foreground">{tipItem}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 pt-4">
          {safetyTips?.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentTip(index)}
              className={cn(
                "h-2 rounded-full transition-all",
                index === currentTip ? "w-8 bg-primary" : "w-2 bg-muted"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SafetyTipsCarousel;